import { Box, Badge, Flex, Button, Accordion, Text } from '@chakra-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillAudio } from 'react-icons/ai';
import { BsPersonCheckFill } from 'react-icons/bs';
import { IoDocumentText } from 'react-icons/io5';
import { RiCloseLine, RiHandCoinLine } from 'react-icons/ri';
import { BiMessageMinus } from 'react-icons/bi';
import { MdWatchLater } from 'react-icons/md';
import { useStoreState } from '../../models/hooks';
import { OfferRejected } from './types';
import CertnLogoSuccess from '../../assets/img/certn-logo-success.svg';
import AppyHereIcon from '../../assets/img/appy_logo.svg';
import colors from '../../styles/colors';
import { TodoAccordionItem } from './TodoAccordionItem';

export type TodoSummaryProps = {
  filterByStatus: string;
  setFilterByStatus: (status: string) => void;
};
export const TodoSummary = React.memo(function TodoSummary({ filterByStatus, setFilterByStatus }: TodoSummaryProps) {
  const { t } = useTranslation('toDoList');
  const { counts: count, interviewCounts } = useStoreState((state) => state.todo);

  const renderCustomBadge = React.useCallback(
    (countNumber: number) =>
      countNumber.toString().length < 3 ? (
        <Box
          mr="6px"
          w="20px"
          lineHeight="20px"
          borderRadius="50%"
          textAlign="center"
          background={colors.red[500]}
          color={colors.white}
          fontSize="14px"
          fontWeight={700}
        >
          {countNumber}
        </Box>
      ) : (
        <Badge background={colors.red[500]} color={colors.white} ml="6px" fontSize="14px" fontWeight={700} borderRadius="12px">
          {countNumber}
        </Badge>
      ),
    [],
  );

  const offerRejectedItems = React.useMemo(() => {
    if (count?.offerRejected) {
      return Object.keys(count.offerRejected)
        .filter((item) => item.startsWith('offerRejected'))
        .reduce(
          (acc, key) => ({ ...acc, [key]: count.offerRejected?.[key as keyof OfferRejected] }),
          {} as { [key: string]: number },
        );
    }
    return {};
  }, [count]);

  const offerExpiredItems = React.useMemo(() => {
    if (count?.offerRejected) {
      return Object.keys(count.offerRejected)
        .filter((item) => item.startsWith('offerExpired'))
        .reduce(
          (acc, key) => ({ ...acc, [key]: count.offerRejected?.[key as keyof OfferRejected] }),
          {} as { [key: string]: number },
        );
    }
    return {};
  }, [count]);

  if (!count) return null;

  const totalUpcomingInterviewCount = interviewCounts?.upcomingInterviews ?? count.upcomingInterviews;

  return (
    <Box borderRight={`1px solid ${colors.dividerGray}`} flexBasis="380px" alignSelf="stretch" flexShrink={0}>
      <Box overflow="auto">
        {filterByStatus && (
          <Flex align="center" justifyContent="space-between" px={3} py={2} borderTop={`1px solid ${colors.neutral[200]}`}>
            <Text fontSize="sm" fontWeight="bold">
              {t('filterBy')}
            </Text>
            <Button
              size="xs"
              onClick={() => setFilterByStatus('')}
              rightIcon={<RiCloseLine />}
              colorScheme="blue"
              variant="outline"
              data-testid="status-reset"
            >
              {t(`${filterByStatus}`)}
            </Button>
          </Flex>
        )}
        <Accordion allowToggle>
          {totalUpcomingInterviewCount > 0 && (
            <TodoAccordionItem
              title="upcomingInterviews"
              icon={<MdWatchLater style={{ marginRight: '4px' }} color={colors.blue.default} size="20px" />}
              totalCount={totalUpcomingInterviewCount}
              filterByStatus={filterByStatus}
              setFilterByStatus={setFilterByStatus}
              renderCustomBadge={renderCustomBadge}
              status="upcomingInterviews"
            />
          )}
          {count.totalHireForm > 0 && (
            <TodoAccordionItem
              title="hireForm"
              icon={<IoDocumentText style={{ marginRight: '4px' }} color={colors.blue.default} size="20px" />}
              totalCount={count.totalHireForm}
              items={{ ...count.hireForm }}
              filterByStatus={filterByStatus}
              setFilterByStatus={setFilterByStatus}
              renderCustomBadge={renderCustomBadge}
            />
          )}
          {count.totalBackgroundCheck > 0 && (
            <TodoAccordionItem
              title="backgroundCheck"
              icon={<img src={CertnLogoSuccess} style={{ width: 24 }} alt="" />}
              totalCount={count.totalBackgroundCheck}
              items={{ ...count.backgroundCheck }}
              filterByStatus={filterByStatus}
              setFilterByStatus={setFilterByStatus}
              renderCustomBadge={renderCustomBadge}
            />
          )}

          {count?.totalWorkbright > 0 && ( // for i9 check
            <TodoAccordionItem
              title="i9Verification"
              icon={<BsPersonCheckFill color={colors.blue.default} size={20} />}
              totalCount={count.totalWorkbright}
              items={{ ...count.workbright }}
              filterByStatus={filterByStatus}
              setFilterByStatus={setFilterByStatus}
              renderCustomBadge={renderCustomBadge}
            />
          )}

          {(count.totalOfferMade > 0 || count.totalOfferAccepted > 0) && (
            <TodoAccordionItem
              title="offer"
              icon={<RiHandCoinLine style={{ marginRight: '4px' }} color={colors.blue.default} size="20px" />}
              totalCount={count.totalOfferMade + count?.totalOfferAccepted}
              items={{ ...count.offerMade, ...count.offerAccepted }}
              filterByStatus={filterByStatus}
              setFilterByStatus={setFilterByStatus}
              renderCustomBadge={renderCustomBadge}
            />
          )}

          {count?.totalOfferRejected > 0 && (
            <TodoAccordionItem
              title="offerRejected"
              icon={<RiHandCoinLine style={{ marginRight: '4px' }} color={colors.blue.default} size="20px" />}
              totalCount={count.totalOfferRejected}
              items={{ ...offerRejectedItems }}
              filterByStatus={filterByStatus}
              setFilterByStatus={setFilterByStatus}
              renderCustomBadge={renderCustomBadge}
            />
          )}

          {count?.totalOfferExpired > 0 && (
            <TodoAccordionItem
              title="offerExpired"
              icon={<RiHandCoinLine style={{ marginRight: '4px' }} color={colors.blue.default} size="20px" />}
              totalCount={count.totalOfferExpired}
              items={offerExpiredItems}
              filterByStatus={filterByStatus}
              setFilterByStatus={setFilterByStatus}
              renderCustomBadge={renderCustomBadge}
            />
          )}
          {count.languageEvaluation > 0 && (
            <TodoAccordionItem
              title="languageEvaluation"
              icon={<AiFillAudio style={{ marginRight: '4px' }} color={colors.orange[500]} size="20px" />}
              totalCount={count.languageEvaluation}
              filterByStatus={filterByStatus}
              setFilterByStatus={setFilterByStatus}
              renderCustomBadge={renderCustomBadge}
              status="languageEvaluation"
            />
          )}
          {count?.dialogflow > 0 && (
            <TodoAccordionItem
              title="conversationalAI"
              icon={<img src={AppyHereIcon} style={{ width: 16, marginRight: '6px' }} alt="" />}
              totalCount={count.dialogflow}
              filterByStatus={filterByStatus}
              setFilterByStatus={setFilterByStatus}
              renderCustomBadge={renderCustomBadge}
              status="dialogflow"
            />
          )}
          {count.pausedCandidates > 0 && (
            <TodoAccordionItem
              title="pausedCandidates"
              icon={<BiMessageMinus style={{ marginRight: '4px' }} color={colors.orange[500]} size="20px" />}
              totalCount={count.pausedCandidates}
              filterByStatus={filterByStatus}
              setFilterByStatus={setFilterByStatus}
              renderCustomBadge={renderCustomBadge}
              status="reviewPausedQuestionAnswer"
            />
          )}
        </Accordion>
      </Box>
    </Box>
  );
});
